import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { message } from "antd";
import {
  createProject,
  deleteProject,
  getProjectLists,
  updateProjectStatus,
  getProjectById,
  getProjectUsers as fetchProjectUsers,
  getCompanyBusinessUnits as fetchCompanyBusinessUnits,
  projectToItemRequisition as apiProjectToItemRequisition,
  getProjectItems as fetchProjectItems,
  getProjectWorkflows as fetchProjectWorkflows,
  triggerProjectWorkflow as apiTriggerProjectWorkflow,
  approveProjectWorkflow as apiApproveProjectWorkflow,
  rejectProjectWorkflow as apiRejectProjectWorkflow,
} from "./projectAPI";

const initialState = {
  loading: false,
  deleteLoading: false,
  updateLoading: false,
  projectList: [],
  pagination: {},
  projectDetails: {},
  projectUsers: [],
  companyBusinessUnits: [],
  projectItems: [],
  itemsLoading: false,
  workflows: [],
  workflowLoading: false,
};

const actions = {
  GETPROJECTLIST: "project/GETPROJECTLIST",
  CREATEPROJECT: "project/CREATEPROJECT",
  DELETEPROJECT: "project/DELETEPROJECT",
  UPDATEPROJECTSTATUS: "project/UPDATEPROJECTSTATUS",
  GETPROJECTDETAILS: "project/GETPROJECTDETAILS",
  GETPROJECTUSERS: "project/GETPROJECTUSERS",
  GETCOMPANYBUSINESSUNITS: "project/GETCOMPANYBUSINESSUNITS",
  PROJECTTOITEMREQUISITION: "project/PROJECTTOITEMREQUISITION",
  GETPROJECTITEMS: "project/GETPROJECTITEMS",
  GETPROJECTWORKFLOWS: "project/GETPROJECTWORKFLOWS",
  TRIGGERPROJECTWORKFLOW: "project/TRIGGERPROJECTWORKFLOW",
  APPROVEPROJECTWORKFLOW: "project/APPROVEPROJECTWORKFLOW",
  REJECTPROJECTWORKFLOW: "project/REJECTPROJECTWORKFLOW",
};

export const getProjects = createAsyncThunk(
  actions.GETPROJECTLIST,
  async (payload) => {
    const response = await getProjectLists(
      payload?.page,
      payload?.per_page,
      payload?.search,
    );
    return response;
  },
);

export const addProject = createAsyncThunk(
  actions.CREATEPROJECT,
  async (payload) => {
    const response = await createProject(payload);
    return response;
  },
);

export const removeProject = createAsyncThunk(
  actions.DELETEPROJECT,
  async (payload) => {
    const response = await deleteProject(payload);
    return response;
  },
);

export const changeProjectStatus = createAsyncThunk(
  actions.UPDATEPROJECTSTATUS,
  async (payload) => {
    const response = await updateProjectStatus(payload);
    return response;
  },
);

export const getProjectDetails = createAsyncThunk(
  actions.GETPROJECTDETAILS,
  async (id) => {
    const response = await getProjectById(id);
    return response;
  },
);

export const getProjectUsers = createAsyncThunk(
  actions.GETPROJECTUSERS,
  async (payload) => {
    const response = await fetchProjectUsers(
      payload?.companyId,
      payload?.projectId,
    );
    return response;
  },
);

export const getCompanyBusinessUnits = createAsyncThunk(
  actions.GETCOMPANYBUSINESSUNITS,
  async (companyId) => {
    const response = await fetchCompanyBusinessUnits(companyId);
    return response;
  },
);

export const projectToItemRequisition = createAsyncThunk(
  actions.PROJECTTOITEMREQUISITION,
  async (payload) => {
    const response = await apiProjectToItemRequisition(payload);
    return response;
  },
);

export const getProjectItems = createAsyncThunk(
  actions.GETPROJECTITEMS,
  async (projectId) => {
    const response = await fetchProjectItems(projectId);
    return response;
  },
);

export const getProjectWorkflows = createAsyncThunk(
  actions.GETPROJECTWORKFLOWS,
  async () => {
    const response = await fetchProjectWorkflows();
    return response;
  },
);

export const triggerProjectWorkflow = createAsyncThunk(
  actions.TRIGGERPROJECTWORKFLOW,
  async (payload) => {
    const response = await apiTriggerProjectWorkflow(payload);
    return response;
  },
);

export const approveProjectWorkflow = createAsyncThunk(
  actions.APPROVEPROJECTWORKFLOW,
  async (payload) => {
    const response = await apiApproveProjectWorkflow(payload);
    return response;
  },
);

export const rejectProjectWorkflow = createAsyncThunk(
  actions.REJECTPROJECTWORKFLOW,
  async (payload) => {
    const response = await apiRejectProjectWorkflow(payload);
    return response;
  },
);

export const projectSlice = createSlice({
  name: "project",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getProjects.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProjects.fulfilled, (state, action) => {
        const { data, pagination, success, message: msg } = action.payload;
        state.loading = false;
        state.projectList = data;
        state.pagination = pagination;
        if (success === false) {
          message.error(msg);
        }
      })
      .addCase(getProjects.rejected, (state, action) => {
        const { message: msg } = action.error;
        state.loading = false;
        message.error(msg);
      });

    builder
      .addCase(addProject.pending, (state) => {
        state.loading = true;
      })
      .addCase(addProject.fulfilled, (state, action) => {
        const { success, message: msg } = action.payload;
        state.loading = false;
        if (success) {
          message.success(msg);
        } else {
          message.error(msg);
        }
      })
      .addCase(addProject.rejected, (state, action) => {
        const { message: msg } = action.error;
        state.loading = false;
        message.error(msg);
      });

    builder
      .addCase(removeProject.pending, (state) => {
        state.deleteLoading = true;
      })
      .addCase(removeProject.fulfilled, (state, action) => {
        const { success, message: msg } = action.payload;
        state.deleteLoading = false;
        if (success) {
          message.success(msg);
        } else {
          message.error(msg);
        }
      })
      .addCase(removeProject.rejected, (state, action) => {
        const { message: msg } = action.error;
        state.loading = false;
        message.error(msg);
      });

    builder
      .addCase(changeProjectStatus.pending, (state) => {
        state.updateLoading = true;
      })
      .addCase(changeProjectStatus.fulfilled, (state, action) => {
        const { success, message: msg } = action.payload;
        state.updateLoading = false;
        if (success) {
          message.success(msg);
        } else {
          message.error(msg);
        }
      })
      .addCase(changeProjectStatus.rejected, (state, action) => {
        const { message: msg } = action.error;
        state.loading = false;
        message.error(msg);
      });

    builder
      .addCase(getProjectDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProjectDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.projectDetails = action.payload?.data;
      })
      .addCase(getProjectDetails.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error.message);
      });

    builder
      .addCase(getProjectUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProjectUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.projectUsers = action.payload?.data;
      })
      .addCase(projectToItemRequisition.pending, (state) => {
        state.itemRequisitionLoading = true;
      })
      .addCase(projectToItemRequisition.fulfilled, (state, action) => {
        state.itemRequisitionLoading = false;
        const { success, message: msg } = action.payload;
        if (success) {
          message.success("Item Requisition Created Successfully");
        } else {
          message.error(msg || "Failed to create requisition");
        }
      })
      .addCase(projectToItemRequisition.rejected, (state) => {
        state.itemRequisitionLoading = false;
        message.error("Something went wrong");
      })
      .addCase(getProjectUsers.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error.message);
      });

    builder
      .addCase(getCompanyBusinessUnits.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCompanyBusinessUnits.fulfilled, (state, action) => {
        state.loading = false;
        state.companyBusinessUnits = action.payload?.data?.businessUnits || [];
      })
      .addCase(getCompanyBusinessUnits.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error.message);
      })
      .addCase(getProjectItems.pending, (state) => {
        state.itemsLoading = true;
      })
      .addCase(getProjectItems.fulfilled, (state, action) => {
        state.itemsLoading = false;
        state.projectItems = action.payload?.data?.allItems || [];
      })
      .addCase(getProjectItems.rejected, (state, action) => {
        state.itemsLoading = false;
        // message.error(action.error.message);
      })
      .addCase(getProjectWorkflows.pending, (state) => {
        state.workflowLoading = true;
      })
      .addCase(getProjectWorkflows.fulfilled, (state, action) => {
        state.workflowLoading = false;
        state.workflows = action.payload?.data || [];
      })
      .addCase(getProjectWorkflows.rejected, (state) => {
        state.workflowLoading = false;
      })
      .addCase(triggerProjectWorkflow.pending, (state) => {
        state.workflowLoading = true;
      })
      .addCase(triggerProjectWorkflow.fulfilled, (state, action) => {
        state.workflowLoading = false;
        if (action.payload?.success) {
          message.success("Workflow triggered successfully");
        } else {
          message.error(
            action.payload?.message || "Failed to trigger workflow",
          );
        }
      })
      .addCase(triggerProjectWorkflow.rejected, (state, action) => {
        state.workflowLoading = false;
        message.error(action.error.message);
      })
      .addCase(approveProjectWorkflow.pending, (state) => {
        state.workflowLoading = true;
      })
      .addCase(approveProjectWorkflow.fulfilled, (state, action) => {
        state.workflowLoading = false;
        if (action.payload?.success) {
          message.success("Project Approved Successfully");
        } else {
          message.error(action.payload?.message || "Failed to approve project");
        }
      })
      .addCase(approveProjectWorkflow.rejected, (state, action) => {
        state.workflowLoading = false;
        message.error(action.error.message);
      })
      .addCase(rejectProjectWorkflow.pending, (state) => {
        state.workflowLoading = true;
      })
      .addCase(rejectProjectWorkflow.fulfilled, (state, action) => {
        state.workflowLoading = false;
        if (action.payload?.success) {
          message.success("Project Rejected Successfully");
        } else {
          message.error(action.payload?.message || "Failed to reject project");
        }
      })
      .addCase(rejectProjectWorkflow.rejected, (state, action) => {
        state.workflowLoading = false;
        message.error(action.error.message);
      });
  },
});

export const projectActions = projectSlice.actions;
export default projectSlice.reducer;
